class customForm extends HTMLElement {
    constructor() {
      super();
  
      this.formId = this.getAttribute('data-form-id');
      this.form = this.querySelector('form');
      this.toggleFormBtn = this.querySelectorAll('[data-show-form]');
      this.successMessageEle = this.querySelector('[data-success-message]');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.skuInput = this.querySelector('#sku');
      this.skuResults = this.querySelector('[data-sku-search-results]');
      this.skuIdInput = this.querySelector('#sku_id');
      this.skuAbortController = null;
      this.skuDebouncedSearch = null;
      this.defaultSkuResultsCache = null;
      this.onDocumentClick = this.handleDocumentClick.bind(this);
    }
  
    connectedCallback() {
      this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      this.toggleFormBtn?.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.successMessageEle) {
            this.successMessageEle.classList.add('d-none');
          };
          this.form.classList.remove('d-none');
        })
      });
  
      this.querySelectorAll('input[type="file"]')?.forEach(input => {
        input.addEventListener('change', (e) => {
          let fileNames = Array.from(e.currentTarget.files).map(file => file.name).join(", ");
          if(e.currentTarget.closest('div').querySelector('[data-file-name]')) e.currentTarget.closest('div').querySelector('[data-file-name]').innerHTML = fileNames || `<span class="text-decoration-underline" style="padding-right: 2px;">Choose File</span><span>or drop here.</span>`;
        })
      })

      this.initSkuPredictiveSearch();
      this.initPhoneInput();
    }

    initPhoneInput() {
      const phoneInput = this.querySelector('#phone');
      if (!phoneInput) return;

      phoneInput.addEventListener('input', (event) => {
        const sanitized = event.currentTarget.value.replace(/[^0-9+\-]/g, '');
        if (sanitized !== event.currentTarget.value) {
          event.currentTarget.value = sanitized;
        }
      });
    }

    debounce(func, wait = 250) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    normalizeSkuForSearch(value) {
      return (value || '').replace(/[-\s]/g, '').toLowerCase();
    }

    skuIncludesQuery(sku, query) {
      const normalizedQuery = this.normalizeSkuForSearch(query);
      if (!normalizedQuery) return false;

      return this.normalizeSkuForSearch(sku).includes(normalizedQuery);
    }

    skusAreEquivalent(skuA, skuB) {
      return this.normalizeSkuForSearch(skuA) === this.normalizeSkuForSearch(skuB);
    }

    getHyphenatedSkuGuess(query) {
      const clean = query.replace(/[-\s]/g, '');
      const match = clean.match(/^([A-Za-z]{2,})(\d+)([A-Za-z]{2})?$/);
      if (!match) return null;

      const [, letters, numbers, suffix] = match;
      return suffix ? `${letters}-${numbers}-${suffix}` : `${letters}-${numbers}`;
    }

    getSingleLetterHyphenatedSkuGuess(query) {
      const clean = query.replace(/[-\s]/g, '');
      const match = clean.match(/^([A-Za-z])(\d+)([A-Za-z]*)$/);
      if (!match) return null;

      const [, letter, numbers, suffix] = match;
      return suffix ? `${letter}-${numbers}${suffix}` : `${letter}-${numbers}`;
    }

    getSkuSearchQueries(query) {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const queries = new Set([trimmed]);

      if (!trimmed.includes('-')) {
        const letterPrefix = trimmed.match(/^([A-Za-z]{2,})/)?.[1];
        if (letterPrefix && trimmed.length > letterPrefix.length) {
          queries.add(letterPrefix);
        }

        const hyphenGuess = this.getHyphenatedSkuGuess(trimmed);
        if (hyphenGuess) queries.add(hyphenGuess);

        const singleLetterHyphenGuess = this.getSingleLetterHyphenatedSkuGuess(trimmed);
        if (singleLetterHyphenGuess) queries.add(singleLetterHyphenGuess);
      }

      return Array.from(queries);
    }

    initSkuPredictiveSearch() {
      if (!this.skuInput || !this.skuResults) return;

      this.skuDebouncedSearch = this.debounce((query) => this.fetchSkuResults(query), 250);

      this.skuInput.addEventListener('input', (event) => {
        const query = event.currentTarget.value.trim();
        this.clearSkuSelection();

        if (!query) {
          this.showDefaultSkuSuggestions();
          return;
        }

        this.skuDebouncedSearch(query);
      });

      this.skuInput.addEventListener('focus', () => {
        const query = this.skuInput.value.trim();
        if (!query) {
          this.showDefaultSkuSuggestions();
          return;
        }
        if (this.skuResults.children.length > 0) this.showSkuResults();
      });

      this.skuInput.addEventListener('blur', () => {
        // Only a picked suggestion is valid (tracked by sku_id).
        if (this.skuInput.value.trim() && !this.skuIdInput?.value) {
          const hasExactMatch = this.applyExactSkuMatchFromSuggestions();
          if (!hasExactMatch && !this.isExactDraftSku(this.skuInput.value) && !this.isExactDefaultSku(this.skuInput.value)) {
            this.skuInput.classList.add('error');
            this.skuInput.setCustomValidity('Please select a SKU from the suggestions');
          }
        } else {
          this.skuInput.classList.remove('error');
          this.skuInput.setCustomValidity('');
        }
      });

      this.skuResults.addEventListener('click', (event) => {
        const optionBtn = event.target.closest('[data-sku-option]');
        if (!optionBtn) return;

        this.skuInput.value = optionBtn.dataset.sku || '';
        if (this.skuIdInput) this.skuIdInput.value = optionBtn.dataset.productId || '';
        this.skuInput.classList.remove('error');
        this.skuInput.setCustomValidity('');
        this.hideSkuResults();
      });

      document.addEventListener('click', this.onDocumentClick);
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.hideSkuResults();
    }

    clearSkuSelection() {
      if (this.skuIdInput) this.skuIdInput.value = '';
    }

    applyExactSkuMatchFromSuggestions() {
      if (!this.skuInput || !this.skuResults || !this.skuIdInput) return false;

      const typedSku = this.skuInput.value.trim();
      if (!typedSku) return false;

      const options = Array.from(this.skuResults.querySelectorAll('[data-sku-option]'));
      const exactOption = options.find((option) => {
        return this.skusAreEquivalent(option.dataset.sku || '', typedSku);
      });

      if (!exactOption) return false;

      this.skuInput.value = exactOption.dataset.sku || this.skuInput.value.trim();
      this.skuIdInput.value = exactOption.dataset.productId || '';
      this.skuInput.classList.remove('error');
      this.skuInput.setCustomValidity('');
      return true;
    }

    getDraftSkus() {
      const raw = this.skuResults?.dataset.skuSearchResults;
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
          ? parsed.map((sku) => (sku || '').trim()).filter(Boolean)
          : [];
      } catch (error) {
        console.error('Failed to parse draft SKU list:', error);
        return [];
      }
    }

    getDefaultSkus() {
      const raw = this.skuResults?.dataset.defaultSkuSuggestions;
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
          ? parsed.map((sku) => (sku || '').trim()).filter(Boolean).slice(0, 10)
          : [];
      } catch (error) {
        console.error('Failed to parse default SKU suggestions:', error);
        return [];
      }
    }

    getDraftSkuResults(query) {
      return this.getDraftSkus()
        .filter((sku) => this.skuIncludesQuery(sku, query))
        .map((sku) => ({
          sku,
          productId: '',
          title: '',
          label: sku
        }));
    }

    isExactDraftSku(sku) {
      const trimmedSku = (sku || '').trim();
      if (!trimmedSku) return false;

      return this.getDraftSkus().some((draftSku) => this.skusAreEquivalent(draftSku, trimmedSku));
    }

    isExactDefaultSku(sku) {
      const trimmedSku = (sku || '').trim();
      if (!trimmedSku) return false;

      return this.getDefaultSkus().some((defaultSku) => this.skusAreEquivalent(defaultSku, trimmedSku));
    }

    mergeSkuResults(apiResults, draftResults) {
      const seen = new Set();
      const merged = [];

      [...apiResults, ...draftResults].forEach((item) => {
        const key = item.sku.toLowerCase();
        if (seen.has(key)) return;

        seen.add(key);
        merged.push(item);
      });

      return merged;
    }

    parseSkuSectionMarkup(sectionMarkup, query) {
      const doc = new DOMParser().parseFromString(sectionMarkup, 'text/html');

      return Array.from(doc.querySelectorAll('[data-sku-option]'))
        .map((option) => {
          const sku = (option.dataset.sku || '').trim();
          const productId = (option.dataset.productId || '').trim();
          const title = (option.dataset.productTitle || '').trim();

          return {
            sku,
            productId,
            title,
            label: option.textContent.trim() || `${sku} - ${title}`
          };
        })
        .filter((item) => item.sku && this.skuIncludesQuery(item.sku, query));
    }

    async fetchSkuApiResultsForQuery(query, filterQuery, signal) {
      const searchFields = 'variants.sku';
      const sectionId = 'sku-predictive-search';
      const predictiveBase = globalThis?.routes?.predictive_search || '/search/suggest';
      const searchQueries = this.getSkuSearchQueries(query);

      const apiResultSets = await Promise.all(
        searchQueries.map(async (searchQuery) => {
          const requestUrl = `${predictiveBase}?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[options][fields]=${encodeURIComponent(searchFields)}&resources[limit]=10&resources[limit_scope]=each&sections=${sectionId}`;
          const response = await fetch(requestUrl, { signal });
          if (!response.ok) throw new Error(`Failed SKU search: ${response.status}`);

          const fetchData = await response.json();
          const sectionMarkup = fetchData[sectionId];
          return sectionMarkup ? this.parseSkuSectionMarkup(sectionMarkup, filterQuery) : [];
        })
      );

      return apiResultSets.reduce(
        (merged, results) => this.mergeSkuResults(merged, results),
        []
      );
    }

    async resolveSkuSuggestionItem(sku, signal) {
      const draftSku = this.getDraftSkus().find((draft) => this.skusAreEquivalent(draft, sku));
      if (draftSku) {
        return {
          sku: draftSku,
          productId: '',
          title: '',
          label: draftSku
        };
      }

      const apiResults = await this.fetchSkuApiResultsForQuery(sku, sku, signal);
      const match = apiResults.find((item) => this.skusAreEquivalent(item.sku, sku));
      if (match) return match;

      return {
        sku,
        productId: '',
        title: '',
        label: sku
      };
    }

    async showDefaultSkuSuggestions() {
      const defaultSkus = this.getDefaultSkus();
      if (!defaultSkus.length) {
        this.clearSkuResults();
        return;
      }

      if (this.skuInput.value.trim()) return;

      if (this.defaultSkuResultsCache) {
        this.renderSkuResults(this.defaultSkuResultsCache);
        return;
      }

      if (this.skuAbortController) this.skuAbortController.abort();
      this.skuAbortController = new AbortController();
      const signal = this.skuAbortController.signal;

      try {
        const resolvedResults = await Promise.all(
          defaultSkus.map((sku) => this.resolveSkuSuggestionItem(sku, signal))
        );

        if (signal.aborted || this.skuInput.value.trim()) return;

        this.defaultSkuResultsCache = resolvedResults;
        this.renderSkuResults(resolvedResults);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Default SKU suggestions error:', error);
          this.clearSkuResults();
        }
      }
    }

    async fetchSkuResults(query) {
      if (this.skuAbortController) this.skuAbortController.abort();
      this.skuAbortController = new AbortController();

      const signal = this.skuAbortController.signal;

      try {
        const apiResults = await this.fetchSkuApiResultsForQuery(query, query, signal);
        const combinedResults = this.mergeSkuResults(apiResults, this.getDraftSkuResults(query));

        if (!combinedResults.length) {
          this.hideSkuResults();
          return;
        }

        this.renderSkuResults(combinedResults);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('SKU predictive search error:', error);
          this.hideSkuResults();
        }
      }
    }

    renderSkuResults(resultItems) {
      this.skuResults.innerHTML = '';

      if (!resultItems.length) {
        this.hideSkuResults();
        return;
      }
      resultItems.slice(0, 10).forEach((item) => {
        const li = document.createElement('li');
        li.className = 'sku-search-result-item';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sku-search-result-btn';
        button.dataset.skuOption = '';
        button.dataset.sku = item.sku;
        button.dataset.productId = item.productId;
        button.dataset.productTitle = item.title;
        button.textContent = item.sku;

        li.appendChild(button);
        this.skuResults.appendChild(li);
      });

      this.showSkuResults();
    }

    showSkuResults() {
      this.skuResults.classList.add('show');
    }

    hideSkuResults() {
      this.skuResults.classList.remove('show');
    }

    clearSkuResults() {
      this.skuResults.innerHTML = '';
      this.hideSkuResults();
    }

    validateForm() {
      let isValid = true;
      let firstInvalidInput = null;
      this.form.querySelectorAll('input, textarea, select').forEach(input => {
          if (input.hasAttribute('required')) {
              let isEmpty = !input.value.trim();

              if (input === this.skuInput) {
                  this.applyExactSkuMatchFromSuggestions();
                  const hasTypedSku = !!this.skuInput.value.trim();
                  const hasPickedSuggestion = !!this.skuIdInput?.value.trim()
                    || this.isExactDraftSku(this.skuInput.value)
                    || this.isExactDefaultSku(this.skuInput.value);
                  if (hasTypedSku && !hasPickedSuggestion) {
                      isValid = false;
                      input.classList.add('error');
                      input.setCustomValidity('Please select a SKU from the suggestions');
                      if (!firstInvalidInput) firstInvalidInput = input;
                      return;
                  }
              }

              if ((input.type === 'radio' || input.type === 'checkbox') && 
                  !this.form.querySelector(`input[name="${input.name}"]:checked`)) {
                  isValid = false;
                  input.classList.add('error');
                  input.setCustomValidity('Please select an option');
                  if (!firstInvalidInput) firstInvalidInput = input;
              } else if (input.type === 'email') {
                  let emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailPattern.test(input.value.trim())) {
                      isValid = false;
                      input.classList.add('error');
                      input.setCustomValidity('Please enter a valid email address');
                      if (!firstInvalidInput) firstInvalidInput = input;
                  } else {
                      input.classList.remove('error');
                      input.setCustomValidity('');
                  }
              } else if (input.type === 'tel') {
                  const phonePattern = /^[0-9+\-]+$/;
                  if (isEmpty) {
                      isValid = false;
                      input.classList.add('error');
                      input.setCustomValidity('This field is required');
                      if (!firstInvalidInput) firstInvalidInput = input;
                  } else if (!phonePattern.test(input.value.trim())) {
                      isValid = false;
                      input.classList.add('error');
                      input.setCustomValidity('Please enter a valid phone number');
                      if (!firstInvalidInput) firstInvalidInput = input;
                  } else {
                      input.classList.remove('error');
                      input.setCustomValidity('');
                  }
              } else if (input.type !== 'radio' && input.type !== 'checkbox' && isEmpty) {
                  isValid = false;
                  input.classList.add('error');
                  input.setCustomValidity('This field is required');
                  if (!firstInvalidInput) firstInvalidInput = input;
              } else {
                  input.classList.remove('error');
                  input.setCustomValidity('');
              }
          }
      });

      if (firstInvalidInput) {
        firstInvalidInput.reportValidity();
      }
      return isValid;
    }

    validateRecaptcha() {
      const recap = this.form.querySelector('.g-recaptcha');
      if (!recap) return true;

      if (typeof reCaptchaAlert4476862000000266091 === 'function') {
        return reCaptchaAlert4476862000000266091();
      }

      const recapErr = this.form.querySelector('[id^="recapErr"]');
      if (recap.getAttribute('captcha-verified') !== 'true') {
        if (recapErr) recapErr.style.visibility = 'visible';
        return false;
      }

      if (recapErr) recapErr.style.visibility = 'hidden';
      return true;
    }

    async getForm() {
      let formData = { formResponse: {} };
      let fileUploadPromises = [];

      this.form.querySelectorAll('input, textarea, select').forEach((input) => {
          if (input.type === 'file' && input.files.length > 0) {
              let fileUploadPromise = this.uploadFile(input).then(data => {
                  formData.formResponse[input.name] = data;
              });
              fileUploadPromises.push(fileUploadPromise);
          } else if (input.type === 'radio' || input.type === 'checkbox') {
              if (input.checked) {
                  if (!formData.formResponse[input.name]) {
                      formData.formResponse[input.name] = input.type === 'checkbox' ? [] : '';
                  }
                  if (input.type === 'checkbox') {
                      formData.formResponse[input.name].push(input.value);
                  } else {
                      formData.formResponse[input.name] = input.value;
                  }
              }
          } else {
              formData.formResponse[input.name] = input.value;
          }
      });
  
      await Promise.all(fileUploadPromises);
      try {
          let response = await fetch(`https://formbuilder.hulkapps.com/forms/create_response/${this.formId}`, {
              method: 'POST',
              headers: {
                  "Content-Type": "application/json",
                  "X-Form-Access-Token": "1cf4f1584c15e993"
              },
              body: JSON.stringify(formData)
          });
          if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
          }
          let data = await response.json();
          if (this.successMessageEle) {
              this.successMessageEle.classList.remove('d-none');
              this.form.classList.add('d-none');
              this.submitBtn.classList.remove('loading');
              // window.bvCallback = function (BV) {
              //   BV.pixel.trackTransaction({
              //       "items" : [
              //           {
              //               "productId" : formData.formResponse.sku_id,
              //           }
              //       ],
              //       "orderId" : Math.floor(Math.random() * 9999999999),
              //       "email" : formData.formResponse.email,
              //       "locale" : "en_US",
              //       "nickname" : formData.formResponse.first_name + ' ' + formData.formResponse.last_name
              //   });
              // };
              // console.log(formData.formResponse);

              const theForm = Array.from(this.form.elements)
                  .filter(el => el.name)
                  .reduce((obj, el) => {
                      obj[el.name] = el.value;
                      return obj;
                  }, {});
              if(theForm && theForm.sku) {
                let contact = {
                "CONTACTCF2": theForm.sku,
                "Email": theForm.Email,
                "actionType":"Q29udGFjdHM=",
                "xmIwtLD":"cdae6f7e17041b95a27e78803bff885d401ad615d77ec2188a56369f5790f5e6e894e67db06aa19704b7cf1906be22d1",
                "zc_gad":"",
                "xnQsjsdp":"5d5dc6757738f08fce8103c42353f099967829e93f6628db619b4d9be5210a9d",
                "Last Name": (`${theForm['First Name']} ${theForm['Last Name']}`).trim(),
                "returnURL": null,
                };
                //end for zoho

                fetch('https://crm.zoho.com/crm/WebToContactForm', {
                method: 'POST',
                body: new URLSearchParams(contact),
                }).catch((err) => console.error('Zoho CRM submission error:', err));
              }

          }

      } catch (error) {
          console.error("Form submission error:", error);
      }
  }

    async uploadFile(field) {
        let files = [];
  
        for (const file of field.files) {
            let individualFormData = new FormData();
            individualFormData.append("file_data", file);
  
            try {
                let response = await fetch(`https://formbuilder.hulkapps.com/forms/file_upload/${this.formId}`, {
                    method: 'POST',
                    headers: { "X-Form-Access-Token": "1cf4f1584c15e993" },
                    body: individualFormData,
                    redirect: 'follow'
                });
  
                let imageResp = await response.json();
  
                if (!response.ok || imageResp.error) {
                    throw new Error(imageResp.error || 'File upload failed');
                }
  
                files.push({
                    url: imageResp.response.url,
                    type: "file",
                    id: imageResp.response.id
                });
            } catch (error) {
                console.error("File upload failed:", error);
                throw error;
            }
        }
  
        return {
            type: "file",
            upload_type: "multiple",
            files: files
        };
    }

    onSubmit(e) {
      e.preventDefault();
      if (this.validateForm() && this.validateRecaptcha()) {
        this.submitBtn.classList.add('loading');
        this.getForm();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.skuAbortController) this.skuAbortController.abort();
    }
  }
  customElements.define('custom-form', customForm);